import {
  Stack,
  Group,
  Button,
  TextInput,
  Card,
  Modal,
  Textarea,
  Alert,
  Table,
  Menu,
  ActionIcon,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { useEffect, useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  IconSearch,
  IconAlertCircle,
  IconCheck,
  IconPencil,
  IconTrash,
  IconPower,
  IconChevronDown,
  IconBuildingCommunity,
} from '@tabler/icons-react';
import {
  useEmpresas,
  useCreateEmpresa,
  useUpdateEmpresa,
  useDeleteEmpresa,
  getErrorMessage,
} from '../../api/hooks';
import type { CreateEmpresaDto, Empresa, EmpresaTipo, UpdateEmpresaDto } from '../../api/types';
import { CatalogPageHeader } from '../../components/CatalogPageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';

/**
 * Tipo de empresa a listar/crear.
 *
 * - `CARGADOR`      → cargadores contractuales.
 * - `TRANSPORTISTA` → transportistas efectivos.
 * - `AMBOS`         → no se filtra (se muestran los tres tipos).
 */
export type TipoEmpresaFiltro = 'CARGADOR' | 'TRANSPORTISTA' | 'AMBOS';

/**
 * Pagina generica para listar, crear, editar y eliminar empresas filtradas
 * por tipo.
 *
 * - CargadoresList      usa tipo="CARGADOR".
 * - TransportistasList  usa tipo="TRANSPORTISTA".
 *
 * El backend expone un unico recurso /empresas con campo `tipo`; aqui
 * filtramos en cliente (el listado es pequeno en MVP).
 *
 * Las empresas con `tipo='AMBOS'` aparecen en ambos catalogos (cargadores
 * Y transportistas) — son empresas mixtas que actuan como cargador y
 * transportista segun el envio.
 */
export interface EmpresasListProps {
  /** Tipo de empresa a listar/crear. */
  tipo: TipoEmpresaFiltro;
  /** Titulo de la pagina. */
  titulo: string;
  /** Subtitulo de la pagina. */
  subtitulo: string;
  /** Texto del boton principal. */
  botonNuevo: string;
  /** Titulo del modal de alta. */
  tituloModal: string;
  /** Mensaje de exito tras crear. */
  exitoTitulo: string;
  /** Texto cuando no hay elementos. */
  emptyHint: string;
  /** Texto del error de carga. */
  errorTitulo: string;
}

interface FormValues {
  razonSocial: string;
  nombreComercial: string;
  nifCif: string;
  domicilioFiscal: string;
  municipio: string;
  codigoPostal: string;
  provincia: string;
  pais: string;
  email: string;
  telefono: string;
}

const initialFormValues: FormValues = {
  razonSocial: '',
  nombreComercial: '',
  nifCif: '',
  domicilioFiscal: '',
  municipio: '',
  codigoPostal: '',
  provincia: '',
  pais: 'ES',
  email: '',
  telefono: '',
};

/**
 * Devuelve true si la empresa, debe aparecer en la lista filtrada por `tipo`.
 * Reglas:
 *   - tipo=CARGADOR      → tipo === 'CARGADOR' || tipo === 'AMBOS'
 *   - tipo=TRANSPORTISTA → tipo === 'TRANSPORTISTA' || tipo === 'AMBOS'
 *   - tipo=AMBOS         → siempre
 */
function matchesTipo(tipoEmpresa: EmpresaTipo, filtro: TipoEmpresaFiltro): boolean {
  if (filtro === 'AMBOS') return true;
  if (tipoEmpresa === 'AMBOS') return true; // una mixta sale en ambos catalogos
  return tipoEmpresa === filtro;
}

export function EmpresasList({
  tipo,
  titulo,
  subtitulo,
  botonNuevo,
  tituloModal,
  exitoTitulo,
  emptyHint,
  errorTitulo,
}: EmpresasListProps) {
  const { data: empresas = [], isLoading, error } = useEmpresas();
  const crear = useCreateEmpresa();
  const actualizar = useUpdateEmpresa();
  const eliminar = useDeleteEmpresa();
  const [opened, { open, close }] = useDisclosure(false);
  const [filter, setFilter] = useState('');
  // Empresa actualmente editandose en el modal. `null` → alta nueva.
  const [editingId, setEditingId] = useState<string | null>(null);
  // Empresa pendiente de confirmar borrado.
  const [toDelete, setToDelete] = useState<Empresa | null>(null);

  const form = useForm<FormValues>({
    initialValues: initialFormValues,
    validate: {
      razonSocial: (v) => (v.trim() ? null : 'Requerido'),
      nifCif: (v) => (v.trim() ? null : 'Requerido'),
      domicilioFiscal: (v) => (v.trim() ? null : 'Requerido'),
    },
  });

  // Cada vez que se abre el modal en modo edicion, precargamos los valores
  // actuales. Si es alta, los valores son los del initial.
  useEffect(() => {
    if (!opened) return;
    if (editingId) {
      const e = empresas.find((x) => x.id === editingId);
      if (e) {
        form.setValues({
          razonSocial: e.razonSocial ?? '',
          nombreComercial: e.nombreComercial ?? '',
          nifCif: e.nifCif ?? '',
          domicilioFiscal: e.domicilioFiscal ?? '',
          municipio: e.municipio ?? '',
          codigoPostal: e.codigoPostal ?? '',
          provincia: e.provincia ?? '',
          pais: e.pais ?? 'ES',
          email: e.email ?? '',
          telefono: e.telefono ?? '',
        });
        form.resetDirty();
      }
    } else {
      form.setValues(initialFormValues);
      form.resetDirty();
    }
    // form no es estable pero no queremos re-sincronizar en cada cambio del
    // form; basta con abrir/cerrar y cambiar de editingId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editingId]);

  const filtradas = useMemo(
    () => empresas.filter((e) => matchesTipo(e.tipo, tipo)),
    [empresas, tipo],
  );

  const filtered = filtradas.filter((e) =>
    [e.razonSocial, e.nifCif, e.email, e.municipio, e.nombreComercial]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(filter.toLowerCase()),
  );

  const handleNew = () => {
    setEditingId(null);
    open();
  };

  const handleEdit = (e: Empresa) => {
    setEditingId(e.id);
    open();
  };

  const handleDelete = (e: Empresa) => setToDelete(e);

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    eliminar.mutate(toDelete.id, {
      onSuccess: () => {
        notifications.show({
          title: 'Empresa eliminada',
          message: `${toDelete.razonSocial} ya no aparece en el catalogo.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        setToDelete(null);
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al eliminar',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  const handleToggleActivo = (e: Empresa) => {
    actualizar.mutate(
      { id: e.id, data: { activo: !e.activo } },
      {
        onSuccess: () =>
          notifications.show({
            title: e.activo ? 'Empresa desactivada' : 'Empresa reactivada',
            message: e.razonSocial,
            color: 'green',
            icon: <IconCheck size={18} />,
          }),
        onError: (err: unknown) =>
          notifications.show({
            title: 'Error al cambiar el estado',
            message: getErrorMessage(err),
            color: 'red',
            icon: <IconAlertCircle size={18} />,
          }),
      },
    );
  };

  const handleSubmit = (values: FormValues) => {
    // Payload comun (sin tocar `activo` — el alta se crea activa por backend).
    const payload: CreateEmpresaDto = {
      tipo,
      razonSocial: values.razonSocial.trim(),
      nifCif: values.nifCif.trim(),
      domicilioFiscal: values.domicilioFiscal.trim(),
      pais: values.pais.trim().toUpperCase() || 'ES',
    };
    const optionalString = (s: string): string | undefined => {
      const t = s.trim();
      return t ? t : undefined;
    };
    if (values.nombreComercial) payload.nombreComercial = optionalString(values.nombreComercial);
    if (values.municipio) payload.municipio = optionalString(values.municipio);
    if (values.codigoPostal) payload.codigoPostal = optionalString(values.codigoPostal);
    if (values.provincia) payload.provincia = optionalString(values.provincia);
    if (values.email) payload.email = optionalString(values.email);
    if (values.telefono) payload.telefono = optionalString(values.telefono);

    if (editingId) {
      // Edicion: PATCH con todos los campos (Partial<CreateEmpresaDto>).
      const updateData: UpdateEmpresaDto = {
        razonSocial: values.razonSocial.trim(),
        nombreComercial: optionalString(values.nombreComercial),
        nifCif: values.nifCif.trim(),
        domicilioFiscal: values.domicilioFiscal.trim(),
        municipio: optionalString(values.municipio),
        codigoPostal: optionalString(values.codigoPostal),
        provincia: optionalString(values.provincia),
        pais: values.pais.trim().toUpperCase() || 'ES',
        email: optionalString(values.email),
        telefono: optionalString(values.telefono),
      };
      actualizar.mutate(
        { id: editingId, data: updateData },
        {
          onSuccess: () => {
            notifications.show({
              title: 'Empresa actualizada',
              message: `${values.razonSocial} guardada correctamente.`,
              color: 'green',
              icon: <IconCheck size={18} />,
            });
            close();
          },
          onError: (e: unknown) =>
            notifications.show({
              title: 'Error al actualizar',
              message: getErrorMessage(e),
              color: 'red',
              icon: <IconAlertCircle size={18} />,
            }),
        },
      );
      return;
    }

    crear.mutate(payload, {
      onSuccess: () => {
        notifications.show({
          title: exitoTitulo,
          message: `${values.razonSocial} ya esta disponible.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        close();
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al dar de alta',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  const modalTitle = editingId ? `Editar ${titulo.toLowerCase()}` : tituloModal;

  return (
    <Stack gap="md">
      <CatalogPageHeader
        title={titulo}
        subtitle={subtitulo}
        icon={IconBuildingCommunity}
        newLabel={botonNuevo}
        count={filtradas.length}
        countColor={tipo === 'AMBOS' ? 'grape' : 'deca'}
        onNew={handleNew}
        filters={
          <TextInput
            placeholder="Buscar por razon social, NIF, email o municipio..."
            leftSection={<IconSearch size={16} />}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            w={280}
          />
        }
      />

      {error && (
        <Alert icon={<IconAlertCircle size={20} />} color="red" variant="light" title={errorTitulo}>
          Verifica tu conexion y vuelve a intentarlo.
        </Alert>
      )}

      <Card withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Razon social</Table.Th>
              <Table.Th>NIF/CIF</Table.Th>
              <Table.Th>Municipio</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th style={{ width: 90, textAlign: 'right' }}>Acciones</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={5}>Cargando...</Table.Td>
              </Table.Tr>
            )}
            {!isLoading && !error && filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  {filtradas.length === 0 ? emptyHint : 'Sin resultados para el filtro.'}
                </Table.Td>
              </Table.Tr>
            )}
            {filtered.map((e) => (
              <Table.Tr key={e.id}>
                <Table.Td>
                  <Stack gap={2}>
                    <Text fw={500}>{e.razonSocial}</Text>
                    {e.nombreComercial && (
                      <Text size="xs" c="dimmed">
                        {e.nombreComercial}
                      </Text>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {e.nifCif}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {e.municipio ?? '—'}
                  {e.provincia ? ` (${e.provincia})` : ''}
                </Table.Td>
                <Table.Td>
                  <StatusBadge activo={e.activo} />
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Menu position="bottom-end" withinPortal shadow="md">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" aria-label="Acciones">
                        <IconChevronDown size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconPencil size={14} />}
                        onClick={() => handleEdit(e)}
                      >
                        Editar
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconPower size={14} />}
                        onClick={() => handleToggleActivo(e)}
                      >
                        {e.activo ? 'Desactivar' : 'Reactivar'}
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => handleDelete(e)}
                      >
                        Eliminar
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={opened} onClose={close} title={modalTitle} centered size="lg">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <Group grow align="flex-start">
              <TextInput
                label="Razon social"
                placeholder="Transportes Demo S.L."
                required
                {...form.getInputProps('razonSocial')}
              />
              <TextInput
                label="NIF/CIF"
                placeholder="B12345678"
                required
                {...form.getInputProps('nifCif')}
              />
            </Group>
            <TextInput
              label="Nombre comercial"
              placeholder="Opcional"
              {...form.getInputProps('nombreComercial')}
            />
            <TextInput
              label="Email"
              placeholder="contacto@empresa.es"
              {...form.getInputProps('email')}
            />
            <Textarea
              label="Domicilio fiscal"
              placeholder="Calle, numero, piso..."
              required
              autosize
              minRows={2}
              {...form.getInputProps('domicilioFiscal')}
            />
            <Group grow>
              <TextInput
                label="Municipio"
                placeholder="Madrid"
                {...form.getInputProps('municipio')}
              />
              <TextInput
                label="Codigo postal"
                placeholder="28013"
                {...form.getInputProps('codigoPostal')}
              />
            </Group>
            <TextInput
              label="Provincia"
              placeholder="Madrid"
              {...form.getInputProps('provincia')}
            />
            <Group grow>
              <TextInput
                label="Pais (ISO-2)"
                placeholder="ES"
                maxLength={2}
                {...form.getInputProps('pais')}
              />
              <TextInput
                label="Telefono"
                placeholder="+34900123456"
                {...form.getInputProps('telefono')}
              />
            </Group>
            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={close}
                disabled={crear.isPending || actualizar.isPending}
              >
                Cancelar
              </Button>
              <Tooltip
                label={
                  tipo === 'AMBOS'
                    ? 'Esta pagina no permite crear empresas mixtas (AMBOS). Elige un catalogo concreto.'
                    : undefined
                }
                disabled={tipo !== 'AMBOS'}
              >
                <Button
                  type="submit"
                  loading={crear.isPending || actualizar.isPending}
                  disabled={tipo === 'AMBOS'}
                >
                  {editingId ? 'Guardar cambios' : 'Dar de alta'}
                </Button>
              </Tooltip>
            </Group>
          </Stack>
        </form>
      </Modal>

      <ConfirmDialog
        opened={toDelete !== null}
        title="Eliminar empresa"
        message={
          toDelete ? `¿Eliminar ${toDelete.razonSocial}? Esta accion no se puede deshacer.` : ''
        }
        confirmLabel="Eliminar"
        confirmColor="red"
        loading={eliminar.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </Stack>
  );
}
